import React, { useCallback, useRef, useState } from 'react';

import { RemixOverlay as RemixOptionsOverlay } from './remix-overlay';
import { RemixThinkingBar } from './remix-thinking-bar';

/**
 * Props for the Remix container composed entry point.
 */
interface RemixContainerProps {
	/** The text content currently selected by the user, shown in the remix options overlay. */
	selectedText: string;
	/** Called when the user dismisses the remix flow (e.g. closes the overlay or preview). */
	onClose: () => void;
	/** Optional callback when the user selects a remix type (e.g. chart, diagram, visual aid, database). */
	onOptionSelect?: (type: string) => void;
}

/**
 * Composed entry point for the Remix feature.
 *
 * Step 1 – the user picks a remix type via RemixOptionsOverlay.
 * Step 2 – the AIFC staging modal opens.
 */
export function RemixContainer({
	selectedText,
	onClose,
	onOptionSelect,
}: RemixContainerProps): React.ReactElement {
	const [isThinking, setIsThinking] = useState(false);
	const thinkingTimeout = useRef<NodeJS.Timeout | null>(null);

	const handleOptionSelect = useCallback(
		(type: string) => {
			setIsThinking(true);
			onOptionSelect?.(type);

			// TODO: Stop thinking state when rovo finished processing
			thinkingTimeout.current = setTimeout(() => {
				setIsThinking(false);
			}, 10000);
		},
		[onOptionSelect],
	);

	const handleStopThinking = useCallback(() => {
		// TODO: Cancel rovo remix request instead of cancelling the timeout
		if (thinkingTimeout.current) {
			clearTimeout(thinkingTimeout.current);
			thinkingTimeout.current = null;
		}
		setIsThinking(false);
	}, []);

	if (isThinking) {
		return <RemixThinkingBar onStop={handleStopThinking} />;
	}

	return (
		<RemixOptionsOverlay
			selectedText={selectedText}
			onClose={onClose}
			onOptionSelect={handleOptionSelect}
		/>
	);
}
